import { Box, Flex, Text, useDisclosure } from "@chakra-ui/react";
import { StyledDashboardLayoutChildContainer } from "@root/components/common/style";
import { DashBoardInput } from "@root/components/dashboard";
import useProjectsDashboard from "@root/hooks/useProjectsDashboard";
import { USER_DASHBOARD_INPUT_NAMES, WEB_PROJECTS } from "@root/webConstants";
import {
  getSearchTabs,
  LEGAL_CHATMODE,
  modelOptions,
  modelOptionsFile,
  modelOptionsWebSearch,
  ROUTE_NAMES,
} from "@root/constants";
import { getThreadLoading } from "@root/store/selectors/entities/thread";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useMemo } from "react";
import { getLoggedInUser } from "@root/store/selectors/entities";
import { getCurrentLanguage } from "@root/store/selectors/features";
import { updateThreadLoading } from "@root/store/slices/entities";
import { useCountryContext } from "@root/context/use-country-context";
import {
  LENDING_OPENED,
  reportDashboardLending,
} from "@root/reporting/dashboard-events";
import { useMainChatContext } from "@root/context/kps-chat-context";
import { useTranslation } from "react-i18next";
import SuggestedQuestionsAndFaqs from "./components/SuggestedQuestionsAndFaqs";
import { useLocation } from "react-router-dom";
import { useProjectsContext } from "@root/context/projects-context";
import { ProjectSelect } from "./components/ProjectSelect";
import { UploadDocument } from "@root/components/dashboard/UploadDashboad.tsx/UploadDocument";
import { getChatType } from "@root/utils";

import { ChatHistoryDrawer } from "@root/components/chat-history/chat-history-drawer";
import RefreshIcon from "@root/assets/icons/history-refresh-icon";
import { CommonButton } from "@root/components/common/button/commonButton";
import PillToggle from "@root/components/dashboard/UploadDashboad.tsx/permit-toggler";
import ModelSelector from "./components/model-selection-toggler.tsx/model-selection";
import { UploadIcon } from "lucide-react";
import ModelDisplay from "./components/model-selection-toggler.tsx/model-text";
import { TourGuide } from "@root/components/tour-guide/TourGuide";
import { TourGuideHoverTooltip } from "@root/components/tour-guide/TourGuideHoverTooltip";
import { RestartTourButton } from "@root/components/tour-guide/RestartTourButton";
import { useLegalChatbotTourGuide } from "@root/hooks/useLegalChatbotTourGuide";
import { useProjectDocumentChatbotTourGuide } from "@root/hooks/useProjectDocumentChatbotTourGuide";
import { useDrawingAnalysisTourGuide } from "@root/hooks/useDrawingAnalysisTourGuide";
import { useMultiDocumentTourGuide } from "@root/hooks/useMultiDocumentTourGuide";
import { useKCSChatbotTourGuide } from "@root/hooks/useKCSChatbotTourGuide";
import { BG_OVERLAY } from "@root/hooks/useTourGuide";

export const UserProjectsDashboard = () => {
  const dispatch = useDispatch();
  const { state, inputChangeHandler, onSubmitHandler } = useProjectsDashboard();
  const { country, isLoading, currentLanguage, OS, browser } =
    useCountryContext();
  const currentUser: any = useSelector(getLoggedInUser);
  const threadLoading = useSelector(getThreadLoading);
  const selectedLanguage = useSelector(getCurrentLanguage);
  const { t } = useTranslation("common");
  const { pathname } = useLocation();
  const isDrawingAnalysis =
    pathname === ROUTE_NAMES.DRAWING_ANALYSIS_DASHBOARD ||
    pathname.startsWith(ROUTE_NAMES.DRAWING_ANALYSIS_CHAT);
  const isLegalChatbot = pathname === ROUTE_NAMES.LEGAL_CHATBOT;
  const isKCSChatbot =
    pathname === ROUTE_NAMES.KCS || pathname.includes("/kcs-chat");
  const isMultiDoc = pathname === ROUTE_NAMES.CHECKLIST;
  const isProjectChatbot =
    pathname === ROUTE_NAMES.DOCUMENT_INTEGRATION_CHATBOT ||
    pathname.includes("/document-chat/");

  // Tour guide setup
  const legalChatbotTour = useLegalChatbotTourGuide();
  const projectChatbotTour = useProjectDocumentChatbotTourGuide();
  const drawingAnalysisTour = useDrawingAnalysisTourGuide();
  const multiDocumentTour = useMultiDocumentTourGuide();
  const kcsChatbotTour = useKCSChatbotTourGuide();

  // Determine which tour guide to use based on current route
  const tourGuide = isKCSChatbot
    ? kcsChatbotTour.kcsChatbotTourGuide
    : isMultiDoc
      ? multiDocumentTour.multiDocumentTourGuide
      : isDrawingAnalysis
        ? drawingAnalysisTour.drawingAnalysisTourGuide
        : isProjectChatbot
          ? projectChatbotTour.projectDocumentChatbotTourGuide
          : legalChatbotTour.legalChatbotTourGuide;

  // Get hover tooltips based on current route
  const modelSelectorHover = legalChatbotTour.modelSelectorHover;
  const pillToggleHover = legalChatbotTour.pillToggleHover;
  const textareaHover = isKCSChatbot
    ? kcsChatbotTour.textareaHover
    : isMultiDoc
      ? multiDocumentTour.textareaHover
      : isDrawingAnalysis
        ? drawingAnalysisTour.textareaHover
        : legalChatbotTour.textareaHover;
  const chatbotDropdownHover = isKCSChatbot
    ? kcsChatbotTour.chatbotDropdownHover
    : isMultiDoc
      ? multiDocumentTour.chatbotDropdownHover
      : isDrawingAnalysis
        ? drawingAnalysisTour.chatbotDropdownHover
        : legalChatbotTour.chatbotDropdownHover;
  const suggestedQuestionsHover = isKCSChatbot
    ? kcsChatbotTour.suggestedQuestionsHover
    : isMultiDoc
      ? multiDocumentTour.suggestedQuestionsHover
      : isDrawingAnalysis
        ? drawingAnalysisTour.suggestedQuestionsHover
        : legalChatbotTour.suggestedQuestionsHover;
  const projectSelectHover = projectChatbotTour.projectSelectHover;
  const projectChatbotDropdownHover =
    projectChatbotTour.projectChatbotDropdownHover;

  // Get refs based on current route
  const modelSelectorRef = legalChatbotTour.modelSelectorRef;
  const pillToggleRef = legalChatbotTour.pillToggleRef;
  const textareaRef = isKCSChatbot
    ? kcsChatbotTour.textareaRef
    : isMultiDoc
      ? multiDocumentTour.textareaRef
      : isDrawingAnalysis
        ? drawingAnalysisTour.textareaRef
        : legalChatbotTour.textareaRef;
  const suggestedQuestionsRef = isKCSChatbot
    ? kcsChatbotTour.suggestedQuestionsRef
    : isMultiDoc
      ? multiDocumentTour.suggestedQuestionsRef
      : isDrawingAnalysis
        ? drawingAnalysisTour.suggestedQuestionsRef
        : legalChatbotTour.suggestedQuestionsRef;
  const projectSelectRef = projectChatbotTour.projectSelectRef;

  const {
    setCurrentPathname,
    selectedProject,
    setSelectedProject,
    docChatMode,
    multiDocUploadView,
    setMultiDocUploadView,
    fetchSuggestedQuestions,
    fetchFaqs,
    setAttachedFile,
    model,
    setModel,
    openProjectsListing,
    sourcesData,
    attachedFile,
    legalChatMode,
    setLegalMode,
  } = useMainChatContext();

  const { fetchProjects, projectsData, projectsOptions } = useProjectsContext();
  const { isOpen, onOpen, onClose } = useDisclosure();

  useEffect(() => {
    setLegalMode(LEGAL_CHATMODE.WEB_SEARCH);
  }, []);

  useEffect(() => {
    setSelectedProject(null);
    setAttachedFile([]);
    if (!isLoading && country) {
      const params: any = {
        currentLanguage,
        country_name: country?.country_name,
        country_code: country?.country_code,
        city: country?.city,
        state: country?.state,
        OS: OS,
        userId: currentUser ? currentUser?.id : null,
        userEmail: currentUser ? currentUser?.email : "Anonymous",
        regMode: currentUser ? currentUser?.regMode : "Anonymous",
        eventName: LENDING_OPENED,
        ip_address: country?.IPv4,
        user_agent: browser,
      };
      reportDashboardLending(params, dispatch);
    }
  }, [isLoading, country]);

  useEffect(() => {
    if (!projectsData?.rows?.length) {
      fetchProjects();
    }
    return () => {
      setMultiDocUploadView(true);
    };
  }, []);

  useEffect(() => {
    setCurrentPathname(pathname);
    setMultiDocUploadView(false); // no upload view
  }, [pathname]);

  const chatType = useMemo(() => {
    return getChatType(pathname);
  }, [pathname]);

  useEffect(() => {
    fetchSuggestedQuestions({
      type: chatType,
    });
    fetchFaqs({
      type: chatType,
    });
  }, [chatType, selectedLanguage]);

  useEffect(() => {
    if (threadLoading) {
      dispatch(updateThreadLoading(false as any));
    }
  }, [threadLoading, dispatch]);

  const isWebSearch = legalChatMode === LEGAL_CHATMODE.WEB_SEARCH;
  const isLawInfoCentre = legalChatMode === LEGAL_CHATMODE.LAW_INFO_CENTER;

  const getChatbotTitle = () => {
    if (isLegalChatbot && isWebSearch) return t("translate.webTitle");
    if (isLegalChatbot && isLawInfoCentre) return t("translate.infoTitle");
    if (isMultiDoc) return t("user_dashboard.multiDoc");
    if (isKCSChatbot) return t("translate.letsExploreKCS");
    if (isDrawingAnalysis) return t("chatHistory.drawingAnalysisChatbot");
    return t("translate.projectdocumentchatbot");
  };

  const getChatbotSubTitle = () => {
    if (isProjectChatbot) {
      return t("translate.projectDocumenttext");
    }
    if (isLegalChatbot) {
      return t("user_dashboard.legalChatbotSubtitle");
    }
    if (isMultiDoc) {
      return t("translate.multidocChatbotSubtitle");
    }
    if (isDrawingAnalysis) {
      return t("chatHistory.drawingAnalysisChatbotSubtitle");
    }
  };

  const getProjectId = () => {
    if (isProjectChatbot && !selectedProject) {
      //if no selected project means integrated mode
      return 3;
    } else if (isProjectChatbot && selectedProject) {
      //if selected project then project mode
      return selectedProject;
    }
    return undefined; // fallback if none of the above
  };

  const renderModelSelector = () => {
    const isPanelVisible = openProjectsListing || sourcesData;
    if (!isLegalChatbot) return null;
    if (isLawInfoCentre || isWebSearch)
      return (
        <Flex
          flexDirection={{
            base: "column",
            md: isPanelVisible ? "column" : "row",
          }}
          gap={2}
        >
          <Box
            ref={modelSelectorRef}
            {...modelSelectorHover.hoverProps}
            bg={
              tourGuide.currentStepData?.id === "model-selector"
                ? BG_OVERLAY.BACKGROUND
                : undefined
            }
            backdropFilter={
              tourGuide.currentStepData?.id === "model-selector"
                ? BG_OVERLAY.BACKDROP_FILTER
                : undefined
            }
          >
            <ModelSelector
              options={
                attachedFile.length > 0
                  ? modelOptionsFile
                  : isLawInfoCentre
                    ? modelOptions
                    : modelOptionsWebSearch
              }
              value={model}
              onChange={setModel}
              onMenuClose={() => {
                // Reset hover state when menu closes to ensure tooltip disappears
                modelSelectorHover.hoverProps.onMouseLeave();
              }}
            />
          </Box>
          {/* Hover tooltip for model selector */}
          {modelSelectorHover.isHovered && modelSelectorHover.tooltipData && (
            <TourGuideHoverTooltip
              title={modelSelectorHover.tooltipData.title}
              description={modelSelectorHover.tooltipData.description}
              targetRef={modelSelectorRef}
              position={modelSelectorHover.tooltipData.position}
              offset={modelSelectorHover.tooltipData.offset}
              enabled={!tourGuide.isActive}
              isVisible={modelSelectorHover.isHovered}
            />
          )}
        </Flex>
      );
    return null;
  };

  const renderMainLayout = () => {
    const isPanelVisible = openProjectsListing || sourcesData;

    return (
      <Flex
        minW={"100%"}
        minH="100vh"
        flexDir="column"
        alignItems="center"
        pr={isPanelVisible ? "400px" : "0"}
        transition="padding-right 0.3s ease"
      >
        <Flex
          justifyContent="space-between"
          width="100%"
          borderBottom="2px solid #E0E0E0"
          alignItems="center"
          p="10px 30px"
          flexShrink={0}
          flexWrap="nowrap"
          gap="15px"
        >
          {!isLegalChatbot && (
            <Box minW="0" flex="1 1 auto">
              <ModelDisplay modelKey={chatType} />
            </Box>
          )}
          <Box minW="0" flex="1 1 auto">
            {renderModelSelector()}
          </Box>
          <Flex
            gap="10px"
            alignItems={"center"}
            flexWrap={{ base: "wrap", md: isPanelVisible ? "wrap" : "nowrap" }}
            justifyContent="flex-end"
            minW="0"
            flexShrink={1}
          >
            {isMultiDoc && (
              <Box
                flexBasis={{
                  base: "calc(50% - 5px)",
                  md: isPanelVisible ? "calc(30% - 5px)" : "auto",
                }}
                minW={{
                  base: "calc(50% - 5px)",
                  md: isPanelVisible ? "calc(30% - 5px)" : "auto",
                }}
              >
                <CommonButton
                  variant="secondary"
                  bg="black"
                  color="white"
                  startIcon={<UploadIcon />}
                  onClick={() => {
                    setCurrentPathname(pathname);
                    setMultiDocUploadView(true);
                  }}
                  fontSize="14px"
                  height="44px"
                  px={6}
                  width={{ base: "100%", md: isPanelVisible ? "100%" : "auto" }}
                  _hover={{
                    bg: "#2f2d2dff", // slightly lighter black
                  }}
                >
                  {t("translate.uploadFile")}
                </CommonButton>
              </Box>
            )}
            <Box
              flexBasis={{
                base: "calc(50% - 5px)",
                md: isPanelVisible ? "calc(30% - 5px)" : "auto",
              }}
              minW={{
                base: "calc(50% - 5px)",
                md: isPanelVisible ? "calc(30% - 5px)" : "auto",
              }}
            >
              <CommonButton
                variant="secondary"
                startIcon={<RefreshIcon />}
                onClick={onOpen}
                fontSize="14px"
                height="44px"
                px={6}
                width={{ base: "100%", md: isPanelVisible ? "100%" : "auto" }}
              >
                {t("translate.viewHistory")}
              </CommonButton>
            </Box>
          </Flex>
        </Flex>

        <ChatHistoryDrawer
          isOpen={isOpen}
          onClose={onClose}
          chatbotId={chatType} // pass the chatbotId dynamically
          mode={legalChatMode}
          projectId={getProjectId()}
        />

        <Flex
          flex="1"
          width="100%"
          alignItems="center"
          justifyContent="center"
          py="20px"
        >
          <StyledDashboardLayoutChildContainer
            paddingLeft={"30px"}
            paddingRight={"30px"}
            justifyContent="center"
            alignItems="center"
            maxW="1200px"
            // margin="0 auto"
            width="100%"
          >
            {isLegalChatbot && (
              <Flex mb="14px" justifyContent="center">
                <Box
                  ref={pillToggleRef}
                  {...pillToggleHover.hoverProps}
                  bg={
                    legalChatbotTour.legalChatbotTourGuide.currentStepData
                      ?.id === "pill-toggle"
                      ? BG_OVERLAY.BACKGROUND
                      : undefined
                  }
                  backdropFilter={
                    legalChatbotTour.legalChatbotTourGuide.currentStepData
                      ?.id === "pill-toggle"
                      ? BG_OVERLAY.BACKDROP_FILTER
                      : undefined
                  }
                >
                  <PillToggle
                    options={getSearchTabs()}
                    active={legalChatMode}
                    setActive={setLegalMode}
                    setState={setLegalMode}
                    disabled={attachedFile.length > 0}
                    tooltipLabel={
                      attachedFile.length > 0
                        ? t("translate.searchModeDisabledTooltip")
                        : undefined
                    }
                  />
                </Box>
                {/* Hover tooltip for pill toggle */}
                {pillToggleHover.isHovered && pillToggleHover.tooltipData && (
                  <TourGuideHoverTooltip
                    title={pillToggleHover.tooltipData.title}
                    description={pillToggleHover.tooltipData.description}
                    targetRef={pillToggleRef}
                    position={pillToggleHover.tooltipData.position}
                    offset={pillToggleHover.tooltipData.offset}
                    enabled={!tourGuide.isActive}
                    isVisible={pillToggleHover.isHovered}
                  />
                )}
              </Flex>
            )}

            <Flex
              justifyContent="center"
              mb="14px"
              flexDirection="column"
              alignItems="center"
              w="100%" // Flex takes full width
              maxW="800px" // Optional: max width for content
              px="20px" // Padding on sides
            >
              <Text
                color="#1A1A1A"
                fontWeight="600"
                fontSize={{ base: "24px", md: "28px", lg: "32px" }}
                textAlign="center"
                lineHeight="1.3"
                w="100%" // Take full width of Flex
              >
                {getChatbotTitle()}
              </Text>

              <Text
                textAlign="center"
                color="#404040"
                w="430px" // Same width as title
                fontWeight="400"
                fontSize={"14px"}
                pt="4px"
              >
                {getChatbotSubTitle()}
              </Text>
            </Flex>

            {isProjectChatbot && (
              <Flex justifyContent="center" mb="14px" position="relative">
                <Box ref={projectSelectRef} {...projectSelectHover.hoverProps}>
                  <ProjectSelect
                    selectedValue={selectedProject}
                    setSelectedValue={setSelectedProject}
                    options={projectsOptions}
                    onMenuClose={() => {
                      // Reset hover state when menu closes to ensure tooltip disappears
                      projectSelectHover.hoverProps.onMouseLeave();
                    }}
                  />
                </Box>
                {/* Hover tooltip for project select */}
                {projectSelectHover.isHovered &&
                  projectSelectHover.tooltipData && (
                    <TourGuideHoverTooltip
                      title={projectSelectHover.tooltipData.title}
                      description={projectSelectHover.tooltipData.description}
                      targetRef={projectSelectRef}
                      position={projectSelectHover.tooltipData.position}
                      offset={projectSelectHover.tooltipData.offset}
                      enabled={!tourGuide.isActive}
                      isVisible={projectSelectHover.isHovered}
                    />
                  )}
              </Flex>
            )}

            {/* 
            {!isProjectChatbot &&
              !isMultiDoc &&
              !isLegalChatbot &&
              !isKCSChatbot &&
              !isGeneratingAnswer && (
                <Box mb="20px">
                  <SuggestedQueryChips onSubmitHandler={onSubmitHandler} />
                </Box>
              )} */}

            <Box width="100%" display="flex" justifyContent="center" mb="16px">
              <Flex width="100%" maxW="750px" flexDirection="column">
                <Box my={"10px"}>
                  <Box
                    ref={textareaRef}
                    {...textareaHover.hoverProps}
                    bg={
                      tourGuide.currentStepData?.id === "textarea" ||
                        tourGuide.currentStepData?.id === "drawing-textarea" ||
                        tourGuide.currentStepData?.id ===
                        "multi-document-textarea" ||
                        tourGuide.currentStepData?.id === "kcs-textarea"
                        ? BG_OVERLAY.BACKGROUND
                        : undefined
                    }
                    backdropFilter={
                      tourGuide.currentStepData?.id === "textarea" ||
                        tourGuide.currentStepData?.id === "drawing-textarea" ||
                        tourGuide.currentStepData?.id ===
                        "multi-document-textarea" ||
                        tourGuide.currentStepData?.id === "kcs-textarea"
                        ? BG_OVERLAY.BACKDROP_FILTER
                        : undefined
                    }
                  >
                    <DashBoardInput
                      pid={WEB_PROJECTS.KSCE_GPT.id}
                      noOfDocs={0}
                      onSubmit={() => null}
                      fileIndex={0}
                      documents={[]}
                      onChangehandler={inputChangeHandler}
                      input={state.doazGptInput}
                      submitHandler={onSubmitHandler}
                      name={USER_DASHBOARD_INPUT_NAMES.DOAZGPT}
                      onChatbotDropdownMenuClose={() => {
                        // Reset hover state when menu closes to ensure tooltip disappears
                        chatbotDropdownHover.hoverProps.onMouseLeave();
                      }}
                      onProjectChatbotDropdownMenuClose={() => {
                        // Reset hover state when menu closes to ensure tooltip disappears
                        projectChatbotDropdownHover.hoverProps.onMouseLeave();
                      }}
                    />
                  </Box>
                </Box>
                {/* Hover tooltip for textarea */}
                {textareaHover.isHovered && textareaHover.tooltipData && (
                  <TourGuideHoverTooltip
                    title={textareaHover.tooltipData.title}
                    description={textareaHover.tooltipData.description}
                    targetRef={textareaRef}
                    position={textareaHover.tooltipData.position}
                    offset={textareaHover.tooltipData.offset}
                    enabled={!tourGuide.isActive}
                    isVisible={textareaHover.isHovered}
                  />
                )}

                <Box
                  ref={suggestedQuestionsRef}
                  {...suggestedQuestionsHover.hoverProps}
                  bg={
                    tourGuide.currentStepData?.id === "suggested-questions" ||
                      tourGuide.currentStepData?.id ===
                      "drawing-suggested-questions" ||
                      tourGuide.currentStepData?.id ===
                      "multi-document-suggested-questions" ||
                      tourGuide.currentStepData?.id === "kcs-suggested-questions"
                      ? BG_OVERLAY.BACKGROUND
                      : undefined
                  }
                  backdropFilter={
                    tourGuide.currentStepData?.id === "suggested-questions" ||
                      tourGuide.currentStepData?.id ===
                      "drawing-suggested-questions" ||
                      tourGuide.currentStepData?.id ===
                      "multi-document-suggested-questions" ||
                      tourGuide.currentStepData?.id === "kcs-suggested-questions"
                      ? BG_OVERLAY.BACKDROP_FILTER
                      : undefined
                  }
                >
                  <SuggestedQuestionsAndFaqs
                    onSubmitHandler={onSubmitHandler}
                  />
                </Box>
                {/* Hover tooltip for suggested questions */}
                {suggestedQuestionsHover.isHovered &&
                  suggestedQuestionsHover.tooltipData && (
                    <TourGuideHoverTooltip
                      title={suggestedQuestionsHover.tooltipData.title}
                      description={
                        suggestedQuestionsHover.tooltipData.description
                      }
                      targetRef={suggestedQuestionsRef}
                      position={suggestedQuestionsHover.tooltipData.position}
                      offset={suggestedQuestionsHover.tooltipData.offset}
                      enabled={!tourGuide.isActive}
                      isVisible={suggestedQuestionsHover.isHovered}
                    />
                  )}
              </Flex>
            </Box>

            {/* Hover tooltip for chatbot dropdown (using selector since it's in dashboardtextarea) */}
            {chatbotDropdownHover.isHovered &&
              chatbotDropdownHover.tooltipData && (
                <TourGuideHoverTooltip
                  title={chatbotDropdownHover.tooltipData.title}
                  description={chatbotDropdownHover.tooltipData.description}
                  targetSelector={
                    isKCSChatbot
                      ? '[data-tour-item="chatbot-dropdown"]'
                      : isMultiDoc || isDrawingAnalysis
                        ? '[data-tour-item="project-chatbot-dropdown"]'
                        : '[data-tour-item="chatbot-dropdown"]'
                  }
                  position={chatbotDropdownHover.tooltipData.position}
                  offset={chatbotDropdownHover.tooltipData.offset}
                  enabled={!tourGuide.isActive}
                  isVisible={chatbotDropdownHover.isHovered}
                />
              )}

            {/* Hover tooltip for project chatbot dropdown */}
            {projectChatbotDropdownHover.isHovered &&
              projectChatbotDropdownHover.tooltipData && (
                <TourGuideHoverTooltip
                  title={projectChatbotDropdownHover.tooltipData.title}
                  description={
                    projectChatbotDropdownHover.tooltipData.description
                  }
                  targetSelector='[data-tour-item="project-chatbot-dropdown"]'
                  position={projectChatbotDropdownHover.tooltipData.position}
                  offset={projectChatbotDropdownHover.tooltipData.offset}
                  enabled={!tourGuide.isActive}
                  isVisible={projectChatbotDropdownHover.isHovered}
                />
              )}

            {/* Start Guide Button */}
            {(isLegalChatbot ||
              isProjectChatbot ||
              isDrawingAnalysis ||
              isMultiDoc ||
              isKCSChatbot) &&
              !tourGuide.isActive && (
                <RestartTourButton onClick={tourGuide.startTour} />
              )}

            {/* Tour Guide Component */}
            {(isLegalChatbot ||
              isProjectChatbot ||
              isDrawingAnalysis ||
              isMultiDoc ||
              isKCSChatbot) &&
              tourGuide.isActive &&
              tourGuide.currentStepData && (
                <TourGuide
                  isActive={tourGuide.isActive}
                  currentStep={tourGuide.currentStepData}
                  currentStepIndex={tourGuide.currentStep}
                  totalSteps={tourGuide.totalSteps}
                  onNext={tourGuide.nextStep}
                  onBack={tourGuide.previousStep}
                  onClose={tourGuide.stopTour}
                  borderRadius={
                    tourGuide.currentStepData?.id === "pill-toggle"
                      ? "50px"
                      : tourGuide.currentStepData?.id === "textarea" ||
                        tourGuide.currentStepData?.id === "drawing-textarea" ||
                        tourGuide.currentStepData?.id ===
                        "multi-document-textarea" ||
                        tourGuide.currentStepData?.id === "kcs-textarea"
                        ? "38px"
                        : tourGuide.currentStepData?.id === "chatbot-dropdown" ||
                          tourGuide.currentStepData?.id ===
                          "drawing-chatbot-dropdown" ||
                          tourGuide.currentStepData?.id ===
                          "multi-document-chatbot-dropdown" ||
                          tourGuide.currentStepData?.id === "kcs-chatbot-dropdown"
                          ? "2px"
                          : "8px"
                  }
                  targetRef={
                    tourGuide.currentStepData.id === "model-selector"
                      ? modelSelectorRef
                      : tourGuide.currentStepData.id === "pill-toggle"
                        ? pillToggleRef
                        : tourGuide.currentStepData.id === "textarea" ||
                          tourGuide.currentStepData.id === "drawing-textarea" ||
                          tourGuide.currentStepData.id ===
                          "multi-document-textarea" ||
                          tourGuide.currentStepData.id === "kcs-textarea"
                          ? textareaRef
                          : tourGuide.currentStepData.id ===
                            "suggested-questions" ||
                            tourGuide.currentStepData.id ===
                            "drawing-suggested-questions" ||
                            tourGuide.currentStepData.id ===
                            "multi-document-suggested-questions" ||
                            tourGuide.currentStepData.id ===
                            "kcs-suggested-questions"
                            ? suggestedQuestionsRef
                            : tourGuide.currentStepData.id === "project-select"
                              ? projectSelectRef
                              : undefined
                  }
                  targetSelector={
                    tourGuide.currentStepData.id === "sidebar-legal" ||
                      tourGuide.currentStepData.id === "chatbot-dropdown" ||
                      tourGuide.currentStepData.id === "sidebar-project" ||
                      tourGuide.currentStepData.id ===
                      "project-chatbot-dropdown" ||
                      tourGuide.currentStepData.id ===
                      "drawing-chatbot-dropdown" ||
                      tourGuide.currentStepData.id ===
                      "multi-document-chatbot-dropdown" ||
                      tourGuide.currentStepData.id === "kcs-chatbot-dropdown"
                      ? tourGuide.currentStepData.targetSelector
                      : undefined
                  }
                />
              )}
          </StyledDashboardLayoutChildContainer>
        </Flex>
      </Flex>
    );
  };

  const renderContent = () => {
    if (isMultiDoc && multiDocUploadView) {
      return <UploadDocument />;
    }
    return renderMainLayout();
  };

  return renderContent();
};
